import {
  LitElement,
  css,
  html,
  classMap,
  styleMap,
  ref,
  createRef,
  enableBodyScroll,
  disableBodyScroll,
} from '@hh/global.js'

class HHDetailsModal extends LitElement {
  static properties = {
    breakpoint: {
      type: String,
    },
    label: {
      type: String,
    },
    selector: {
      type: String,
    },
    open: {
      type: Boolean,
      attribute: 'is-open',
      reflect: true,
    },
    forcedOpen: {
      attribute: 'open',
    },
    lockHeight: {
      type: Boolean,
      attribute: 'lock-height',
    },
    heightOffset: {
      type: Number,
      attribute: 'height-offset',
    },
    _height: {
      state: true,
    },
    hasOpenClass: {
      type: Boolean,
    },
    focused: {
      reflect: true,
    },
    mousedown: {
      reflect: true,
    },
    hoverEnabled: {
      type: Boolean,
      attribute: 'hover-enabled',
    },
  }

  $container = createRef()
  $details = createRef()

  constructor() {
    super()

    this.open = false
    this.lockHeight = false
    this.heightOffset = 3
    this.breakpoint = '(min-width: 750px)'
    this.desktopBreakpoint = '(min-width: 990px)'
    this.bodyListener = this.bodyListener.bind(this)
    this.hoverEnabled = false
  }

  connectedCallback() {
    super.connectedCallback()
    document.body.addEventListener('closeModal', this.close.bind(this))
  }

  firstUpdated() {
    this.showMenuOnHover()
  }

  showMenuOnHover() {
    if (this.hoverEnabled && this.isDesktop()) {
      let hoverTimeout

      this.$container.value.addEventListener('mouseenter', () => {
        clearTimeout(hoverTimeout)
        if (!this.open) this.toggle()
      })

      this.$container.value.addEventListener('mouseleave', () => {
        hoverTimeout = setTimeout(() => {
          if (this.open) this.toggle()
        }, 500)
      })
    }
  }

  updated() {
    if (this.isMobile()) {
      this.setBodyScroll(!this.open)
    }
  }

  requestUpdate(name, value) {
    // Allows parents to close open modals
    if (name === 'forcedOpen') {
      this.toggle(false, !this.forcedOpen)
    }
    return super.requestUpdate(name, value)
  }

  interceptEvent(e) {
    e._intercepted = true
  }

  toggle(e = false, value = undefined) {
    // if (e.type === 'keyup' && e.code.toUpperCase() !== 'ENTER') {
    //   return true
    // }

    e && e.preventDefault()

    const isOpen = typeof value === 'undefined' ? !this.open : value
    if (isOpen) {
      this.open = isOpen
      this.hasOpenClass = isOpen
      document.body.classList.remove('mini-cart-is-active')
    } else {
      this.hasOpenClass = false
      this.handleCloseAnimation()
    }
    this[`${this.hasOpenClass ? 'add' : 'remove'}BodyListener`]()
    this.hasOpenClass && this.lockHeight && this.setHeight()
    this.broadcast()
  }

  handleCloseAnimation() {
    let animationStart

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time
      }

      const elapsedTime = time - animationStart
      elapsedTime < 500
        ? window.requestAnimationFrame(handleAnimation)
        : (this.open = false)
    }

    window.requestAnimationFrame(handleAnimation)
  }

  closeFromDetails(e) {
    const path = e.composedPath()
    if (
      path.some((element) => {
        return element.matches && element.matches('[data-close]')
      })
    ) {
      this.toggle()
    }
  }

  closeWithEscape(e) {
    if (e.code.toUpperCase() === 'ESCAPE') {
      this.toggle()
    }
  }

  close() {
    this.open && this.toggle()
  }

  isMobile() {
    return !window.matchMedia(this.breakpoint).matches
  }

  isDesktop() {
    return window.matchMedia(this.desktopBreakpoint).matches
  }

  setBodyScroll(enable = true) {
    if (enable) {
      document.body.style.position = ''
      document.body.style.overflow = ''
    } else {
      document.body.style.position = 'fixed'
      document.body.style.overflow = 'hidden'
    }
  }

  addBodyListener() {
    document.body.addEventListener('click', this.bodyListener)
  }

  removeBodyListener() {
    document.body.removeEventListener('click', this.bodyListener)
  }

  bodyListener(e) {
    !e._intercepted && this.open && this.toggle()
  }

  setHeight() {
    setTimeout(this.initHeight.bind(this), 0)
  }

  initHeight() {
    const { top: parentTop = 0 } = this.parentNode.getBoundingClientRect() || {}
    const { top: detailsTop = 0 } =
      this.$details?.value.getBoundingClientRect() || {}

    this._height = this.isMobile()
      ? `calc(100dvh - ${Math.floor(
          parentTop + detailsTop - this.heightOffset
        )}px)`
      : ''

    document.documentElement.style.setProperty(
      '--header-details-offset',
      `calc(100dvh - ${Math.floor(
        parentTop + detailsTop - this.heightOffset
      )}px)`
    )
  }

  broadcast() {
    const eventType = this.hasOpenClass ? 'modalOpened' : 'modalClosed'
    this.dispatchEvent(
      new CustomEvent(eventType, {
        bubbles: true,
        composed: true,
        detail: {
          value: this,
        },
      })
    )
  }

  onFocus(e) {
    if (this.mousedown) {
      this.focused = false
    } else {
      this.focused = true
    }
  }

  onMouseDown(e) {
    this.mousedown = true
  }

  onFocusOut(e) {
    this.focused = false
    this.mousedown = false
  }

  render() {
    return html` <!-- @ts-ignore no-incompatible-type-binding -->
      <details
        ?open="${this.open}"
        @click="${this.interceptEvent}"
        class="${classMap({ 'is-open': this.hasOpenClass })}"
        ${ref(this.$container)}
      >
        <summary
          @click="${this.toggle}"
          aria-expanded="${this.open}"
          aria-controls="hh-details-modal-panel"
          aria-label="${this.label || 'Menu'}"
          role="button"
          part="summary"
          @focusin="${this.onFocus}"
          @focusout="${this.onFocusOut}"
          @mousedown="${this.onMouseDown}"
        >
          <slot
            class="${classMap({ 'is-open': this.hasOpenClass })}"
            name="summary"
            tabindex="-1"
          ></slot>
        </summary>
        <div
          class="content"
          id="hh-details-modal-panel"
          role="dialog"
          aria-modal="true"
          aria-label="${this.label}"
          style="${styleMap({ height: this._height })}"
          ${ref(this.$details)}
        >
          <hh-focus-trap active="${this.open}">
            <slot
              name="details"
              @keyup="${this.closeWithEscape}"
              @click="${this.closeFromDetails}"
            ></slot>
          </hh-focus-trap>
        </div>
      </details>`
  }

  static styles = css`
    summary {
      list-style-type: none;
    }

    summary {
      position: relative;
      z-index: 2;
      touch-action: manipulation;
    }

    summary:focus {
      outline: 0;
      outline-offset: var(--hh-focus-outline-offset);
    }

    summary::-webkit-details-marker {
      display: none;
    }

    .content {
      position: var(--hh-position, absolute);
      top: var(--hh-top);
      left: 0;
      right: 0;
      bottom: 0;
      background: var(--hh-color-reverse);
      height: var(--hh-modal-height);
      z-index: var(--hh-z-index-inner, 1);
      opacity: 0;
      visibility: hidden;
      overflow-y: auto;
      pointer-events: none;
      transition:
        opacity var(--hh-duration-default, 300ms),
        visibility var(--hh-duration-default, 300ms);
    }

    .is-open .content {
      visibility: visible;
      opacity: 1;
      pointer-events: auto;
    }

    :host([transition-direction='left']) .content {
      transform: translateX(-100%);
      transition:
        opacity var(--hh-duration-default, 300ms),
        visibility var(--hh-duration-default, 300ms),
        transform var(--hh-duration-default, 300ms);
    }

    :host([transition-direction='top']) .content {
      transform: translateY(-67vh);
      opacity: 1;
      visibility: visible;
      transition: transform var(--hh-duration-long, 500ms) ease-in-out;
    }

    :host([transition-direction='top']) .is-open .content,
    :host([transition-direction='left']) .is-open .content {
      transform: none;
    }

    ::slotted([slot='details']) {
      width: 100%;
      display: flex;
    }

    :host {
      z-index: var(--hh-z-index, 100);
    }

    :host([focused='true']) summary {
      outline: 0.2rem solid var(--hh-color-focus);
      outline-offset: 0;
    }

    @media screen and (min-width: 990px) {
      .content {
        overflow-y: visible;
        background: transparent;
      }
    }
  `
}

customElements.define('hh-details-modal', HHDetailsModal)
